import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, doc, updateDoc, getDocs, serverTimestamp, query, where } from 'firebase/firestore';
import { db, auth } from '../../config/firebase';
import { useOrgData } from '../../hooks/useOrgData';
import { logAudit } from '../../utils/auditLog';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import ConfirmDialog from '../UI/ConfirmDialog';

const newLine = () => ({ id: Date.now() + Math.random(), category: '', fund: '', amount: '', description: '' });

const buildLinesFromTransaction = (tx) => {
  if (tx?.type === 'Starting Balance') return [];
  if (tx?.lines?.length) return tx.lines.map(l => ({ ...l, category: l.type || '', fund: l.classification || '', id: Date.now() + Math.random() }));
  return [{ id: Date.now(), category: tx?.type || '', fund: tx?.classification || '', amount: tx?.expense > 0 ? tx.expense : (tx?.income || ''), description: tx?.description || '' }];
};

const initialKind = (tx) => {
  if (!tx) return 'receipt';
  if (tx.type === 'Starting Balance') return 'starting-balance';
  return tx.expense > 0 ? 'bill' : 'receipt';
};

// ── Donor autocomplete ────────────────────────────────────────────────────────

const DonorInput = ({ value, onChange, orgId }) => {
  const [donors, setDonors] = useState([]);
  const [open, setOpen]     = useState(false);
  const containerRef        = useRef(null);

  useEffect(() => {
    if (!orgId) return;
    getDocs(collection(db, 'orgs', orgId, 'donors')).then(snap => {
      setDonors(snap.docs.map(d => d.data().name).filter(Boolean).sort());
    });
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = value.trim()
    ? donors.filter(d => d.toLowerCase().includes(value.toLowerCase()) && d.toLowerCase() !== value.toLowerCase())
    : donors;

  const select = (name) => { onChange(name); setOpen(false); };

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="e.g. John Smith"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto text-sm">
          {filtered.map(d => (
            <li key={d} onMouseDown={() => select(d)} className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-slate-700">
              {d}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// ── Payee autocomplete ────────────────────────────────────────────────────────

const PayeeInput = ({ value, onChange, orgId }) => {
  const [payees, setPayees]     = useState([]);
  const [open, setOpen]         = useState(false);
  const containerRef            = useRef(null);

  // Load saved payees once
  useEffect(() => {
    if (!orgId) return;
    getDocs(collection(db, 'orgs', orgId, 'payees')).then(snap => {
      setPayees(snap.docs.map(d => d.data().name).filter(Boolean).sort());
    });
  }, [orgId]);

  const filtered = value.trim()
    ? payees.filter(p => p.toLowerCase().includes(value.toLowerCase()) && p.toLowerCase() !== value.toLowerCase())
    : payees;

  const select = (name) => {
    onChange(name);
    setOpen(false);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <input
        type="text"
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="e.g. Central Maine Power"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto text-sm">
          {filtered.map(p => (
            <li
              key={p}
              onMouseDown={() => select(p)}
              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-slate-700"
            >
              {p}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Auto-create a bare-bones payee record (name only) the first time a new name
// is used on a transaction — enriched later in Admin → Payees for 1099 prep.
const savePayee = async (orgId, name) => {
  const trimmed = name?.trim();
  if (!trimmed) return;
  const snap = await getDocs(query(collection(db, 'orgs', orgId, 'payees'), where('name', '==', trimmed)));
  if (!snap.empty) return;
  await addDoc(collection(db, 'orgs', orgId, 'payees'), {
    name: trimmed, businessName: '', address: '', city: '', state: '', zip: '',
    phone: '', email: '', tin: '', tinType: 'EIN', exempt1099: true, notes: '',
  });
};

// ── TransactionForm ───────────────────────────────────────────────────────────

const TransactionForm = ({ transaction, orgId, onClose, onDelete }) => {
  const { accounts, funds, transactionTypes } = useOrgData(orgId);
  const activeAccounts = accounts.filter(a => a.active !== false).map(a => a.name);
  const activeFunds    = funds.filter(f => f.active !== false);

  const today  = new Date().toISOString().split('T')[0];
  const isEdit = !!transaction;

  const [date,       setDate]       = useState(transaction?.date       || today);
  const [account,    setAccount]    = useState(transaction?.account    || '');
  const [kind,       setKind]       = useState(() => initialKind(transaction));
  const [cleared,    setCleared]    = useState(transaction?.cleared    || false);
  const [checkNum,   setCheckNum]   = useState(transaction?.checkNum   || '');
  const [payableTo,  setPayableTo]  = useState(transaction?.payableTo  || '');
  const [donor,      setDonor]      = useState(transaction?.donor      || '');
  const [lines,      setLines]      = useState(() => buildLinesFromTransaction(transaction));
  const [sbFund,     setSbFund]     = useState(transaction?.type === 'Starting Balance' ? (transaction.classification || '') : '');
  const [sbAmount,   setSbAmount]   = useState(transaction?.type === 'Starting Balance' ? (transaction.income || '') : '');
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');

  const dialogRef = useFocusTrap(onClose);

  // Default account once loaded
  useEffect(() => {
    if (!account && activeAccounts.length > 0) setAccount(activeAccounts[0]);
  }, [activeAccounts.length]); // eslint-disable-line

  // Funds linked to current account (fallback: all active funds)
  const fundsForAccount = activeFunds.filter(f => f.account === account);
  const fundOptions = fundsForAccount.length > 0 ? fundsForAccount : activeFunds;

  const firestoreKind  = kind === 'bill' ? 'expense' : 'income';
  const filteredTypes  = transactionTypes.filter(t => t.kind === firestoreKind && t.active !== false);

  const addLine    = () => setLines(ls => [...ls, newLine()]);
  const removeLine = (id) => setLines(ls => ls.filter(l => l.id !== id));
  const updateLine = (id, field, value) =>
    setLines(ls => ls.map(l => l.id === id ? { ...l, [field]: value } : l));

  const handleCategoryChange = (id, category) => {
    const txType = transactionTypes.find(t => t.name === category);
    setLines(ls => ls.map(l => l.id === id
      ? { ...l, category, fund: txType?.fund && !l.fund ? txType.fund : l.fund }
      : l
    ));
  };

  // Remembers each kind's lines across toggles, so switching Money Out → Money In → Money Out
  // restores what was already entered instead of clearing it every time.
  const kindLinesRef = useRef({});
  const [pendingKind, setPendingKind] = useState(null);

  const handleKindClick = (k) => {
    if (k === kind) return;
    if (cleared) { setPendingKind(k); return; }
    changeKind(k);
  };

  const changeKind = (k) => {
    if (k === kind) return;

    if (kind === 'bill' || kind === 'receipt') {
      kindLinesRef.current[kind] = lines;
    }

    setKind(k);

    if (k === 'bill' || k === 'receipt') {
      const cached = kindLinesRef.current[k];
      if (cached?.length) {
        setLines(cached);
      } else {
        // First time visiting this kind — keep amounts/descriptions already
        // entered, just clear category/fund since those lists are kind-specific.
        setLines(ls => ls.length ? ls.map(l => ({ ...l, category: '', fund: '' })) : [newLine()]);
      }
    }
  };

  const totalAmount = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!account) { setError('Please select an account.'); return; }

    setSaving(true);
    setError('');

    let data;

    if (kind === 'starting-balance') {
      if (!sbFund)                 { setError('Please select a fund.'); setSaving(false); return; }
      if (!(Number(sbAmount) > 0)) { setError('Please enter a valid amount.'); setSaving(false); return; }
      data = {
        date,
        stmtMonth:      date.substring(0, 7),
        account,
        type:           'Starting Balance',
        classification: sbFund,
        description:    'Starting Balance',
        income:         Number(sbAmount),
        expense:        0,
        year:           Number(date.substring(0, 4)),
        split:          false,
        cleared:        false,
        lines:          [],
      };
    } else {
      if (lines.some(l => !l.category))             { setError('Please select a category for each line.'); setSaving(false); return; }
      if (lines.some(l => !(Number(l.amount) > 0))) { setError('Please enter a valid amount for each line.'); setSaving(false); return; }

      const builtLines = lines.map(l => {
        const t = transactionTypes.find(t => t.name === l.category);
        return { type: l.category, classification: l.fund || t?.fund || '', amount: Number(l.amount), description: l.description };
      });

      const isSplit = lines.length > 1;
      data = {
        date,
        stmtMonth:      date.substring(0, 7),
        account,
        cleared,
        split:          isSplit,
        type:           isSplit ? 'Split' : builtLines[0].type,
        classification: isSplit ? ''      : builtLines[0].classification,
        description:    isSplit ? ''      : builtLines[0].description,
        expense:        kind === 'bill'    ? totalAmount : 0,
        income:         kind === 'receipt' ? totalAmount : 0,
        year:           Number(date.substring(0, 4)),
        lines:          builtLines,
        checkNum:       checkNum.trim() || '',
        payableTo:      kind === 'bill'    ? payableTo.trim() : '',
        donor:          kind === 'receipt' ? donor.trim()    : '',
      };
    }

    try {
      if (isEdit) {
        await updateDoc(doc(db, 'orgs', orgId, 'transactions', transaction.id), data);
        await logAudit(orgId, 'Transaction Edited', {
          account: data.account, type: data.type, date: data.date,
          amount: data.income || data.expense,
        });
      } else {
        const u = auth.currentUser;
        data.createdAt = serverTimestamp();
        data.createdBy = {
          uid:         u?.uid || '',
          email:       u?.email || '',
          displayName: u?.displayName || u?.email || 'Unknown',
        };
        await addDoc(collection(db, 'orgs', orgId, 'transactions'), data);
        await logAudit(orgId, 'Transaction Added', {
          account: data.account, type: data.type, date: data.date,
          amount: data.income || data.expense,
        });
      }

      // Persist payee for future autocomplete
      if (kind === 'bill' && payableTo.trim()) {
        await savePayee(orgId, payableTo.trim());
      }

      onClose();
    } catch (err) {
      console.error('Save error:', err);
      setError(err?.message || 'Failed to save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <>
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="transaction-form-title"
        tabIndex={-1}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 id="transaction-form-title" className="text-lg font-bold text-slate-800">
            {isEdit ? 'Edit Transaction' : 'Add Transaction'}
          </h3>
          <button onClick={onClose} aria-label="Close" title="Close" data-autofocus-skip className="text-gray-500 hover:text-gray-600 text-2xl leading-none">×</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && (
            <p className="text-red-600 text-sm bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{error}</p>
          )}

          {/* Kind toggle */}
          <div className="flex rounded-xl overflow-hidden border border-gray-300">
            <button type="button" onClick={() => handleKindClick('bill')}
              className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                kind === 'bill' ? 'bg-red-500 text-white' : 'bg-white text-slate-500 hover:bg-red-50'
              }`}>
              Money Out
            </button>
            <button type="button" onClick={() => handleKindClick('receipt')}
              className={`flex-1 py-2.5 text-sm font-semibold transition-colors border-l border-gray-300 ${
                kind === 'receipt' ? 'bg-green-500 text-white' : 'bg-white text-slate-500 hover:bg-green-50'
              }`}>
              Money In
            </button>
            <button type="button" onClick={() => handleKindClick('starting-balance')}
              className={`flex-1 py-2.5 text-sm font-semibold transition-colors border-l border-gray-300 ${
                kind === 'starting-balance' ? 'bg-slate-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
              }`}>
              Starting Balance
            </button>
          </div>

          {/* Date + Account */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Date *</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Account *</label>
              <select value={account} onChange={e => setAccount(e.target.value)} required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                <option value="">Select account…</option>
                {activeAccounts.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>

          {/* Donor (Money In only) */}
          {kind === 'receipt' && (
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">Donor <span className="font-normal text-slate-500">(optional)</span></label>
              <DonorInput value={donor} onChange={setDonor} orgId={orgId} />
            </div>
          )}

          {/* Payable To + Check # (Money Out only) */}
          {kind === 'bill' && (
            <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Payable To <span className="font-normal text-slate-500">(optional)</span></label>
                <PayeeInput value={payableTo} onChange={setPayableTo} orgId={orgId} />
              </div>
              <div className="w-32">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Check # <span className="font-normal text-slate-500">(optional)</span></label>
                <input
                  type="text"
                  value={checkNum}
                  onChange={e => setCheckNum(e.target.value)}
                  placeholder="e.g. 1042"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
              </div>
            </div>
          )}

          {/* Starting Balance mode */}
          {kind === 'starting-balance' && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
              <p className="text-xs text-slate-500">
                Sets the opening balance for a fund. Not counted as income or expense.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Fund *</label>
                  <select value={sbFund} onChange={e => setSbFund(e.target.value)} required
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">Select fund…</option>
                    {fundOptions.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Opening Balance *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm select-none">$</span>
                    <input
                      type="number" step="0.01" min="0"
                      value={sbAmount}
                      onChange={e => setSbAmount(e.target.value)}
                      onWheel={e => e.target.blur()}
                      placeholder="0.00"
                      required
                      className="no-spinner w-full border border-gray-300 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bill / Receipt lines */}
          {kind !== 'starting-balance' && (
            <div>
              {lines.length > 1 && (
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-slate-600">
                    Lines ({lines.length}) — Split
                  </label>
                </div>
              )}

              <div className="space-y-2">
                {lines.map((line, index) => (
                  <div key={line.id} className="border border-gray-200 rounded-xl p-3 bg-slate-50/50">
                    {lines.length > 1 && (
                      <div className="text-xs font-semibold text-slate-500 mb-2">Split {index + 1}</div>
                    )}
                    {/* Category + Amount */}
                    <div className="grid grid-cols-[1fr_auto] gap-2 mb-2">
                      <select
                        value={line.category}
                        onChange={e => handleCategoryChange(line.id, e.target.value)}
                        required
                        className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 w-full"
                      >
                        <option value="">Category…</option>
                        {filteredTypes.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                      </select>
                      <div className="relative w-32">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm select-none">$</span>
                        <input
                          type="number" step="0.01" min="0.01"
                          value={line.amount}
                          onChange={e => updateLine(line.id, 'amount', e.target.value)}
                          onWheel={e => e.target.blur()}
                          placeholder="0.00"
                          required
                          className="no-spinner w-full border border-gray-300 rounded-lg pl-7 pr-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                        />
                      </div>
                    </div>
                    {/* Fund + Description */}
                    <div className="flex gap-2 items-center">
                      <select
                        value={line.fund}
                        onChange={e => updateLine(line.id, 'fund', e.target.value)}
                        className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 w-36 flex-shrink-0"
                      >
                        <option value="">Fund…</option>
                        {activeFunds.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                      </select>
                      <input
                        type="text"
                        value={line.description}
                        onChange={e => updateLine(line.id, 'description', e.target.value)}
                        placeholder="Description (optional)"
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                      />
                      {lines.length > 1 && (
                        <button type="button" onClick={() => removeLine(line.id)}
                          aria-label={`Remove split ${index + 1}`} title="Remove split"
                          className="w-6 h-6 rounded-full bg-red-100 text-red-500 hover:bg-red-200 font-bold text-sm flex items-center justify-center flex-shrink-0"
                        >−</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" onClick={addLine}
                className="mt-2 flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                <span className="w-5 h-5 rounded-full bg-blue-100 hover:bg-blue-200 flex items-center justify-center font-bold text-base leading-none">+</span>
                Add split
              </button>
            </div>
          )}

          {/* Split total */}
          {kind !== 'starting-balance' && lines.length > 1 && (
            <div className={`text-right text-sm font-semibold ${kind === 'bill' ? 'text-red-600' : 'text-green-600'}`}>
              Total: ${totalAmount.toFixed(2)}
            </div>
          )}

          {/* Cleared (not shown for starting balance) */}
          {kind !== 'starting-balance' && (
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
              <input type="checkbox" checked={cleared} onChange={e => setCleared(e.target.checked)} className="rounded" />
              <span>Cleared</span>
              <span className="text-xs text-slate-500">(seen on bank statement)</span>
            </label>
          )}

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
            <button type="submit" disabled={saving}
              className={`flex-1 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                kind === 'bill'             ? 'bg-red-500 hover:bg-red-600' :
                kind === 'starting-balance' ? 'bg-slate-600 hover:bg-slate-700' :
                'bg-green-500 hover:bg-green-600'
              }`}
            >
              {saving ? 'Saving…' : isEdit ? 'Save Changes' :
                kind === 'bill'             ? 'Add Money Out' :
                kind === 'starting-balance' ? 'Set Starting Balance' :
                'Add Money In'}
            </button>
            <button type="button" onClick={onClose}
              className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            {onDelete && (
              <button type="button" onClick={onDelete} aria-label="Delete transaction" title="Delete transaction"
                className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </form>
      </div>
    </div>

    {pendingKind && (
      <ConfirmDialog
        title="Change transaction type?"
        message="This transaction has already been cleared. Changing between Money In and Money Out will change its type and may affect your reconciled balance."
        onConfirm={() => { changeKind(pendingKind); setPendingKind(null); }}
        onCancel={() => setPendingKind(null)}
        confirmLabel="Continue"
        danger={false}
      />
    )}
    </>
  );
};

export default TransactionForm;
